import { observer } from 'mobx-react'
import { useRouter } from 'next/router'
import React, { useContext, useRef, useState } from 'react'

import AdminPostParentStatusFilter from '@/components/admin/posts/filters/AdminPostParentStatusFilter'
import AdminPostStatusFilter from '@/components/admin/posts/filters/AdminPostStatusFilter'
import type { IPostStatusChangeModalRef } from '@/components/posts/singlePost/PostStatusChangeModal'
import PostStatusChangeModal from '@/components/posts/singlePost/PostStatusChangeModal'
import Status from '@/components/posts/Status'
import type { ISelectOption } from '@/components/shared/ui/Select/Select'
import Typography from '@/components/shared/ui/Typography'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import { ENTITIES, EVENT_ACTION_TYPES, EventEmitter } from '@/lib/eventEmitter'
import { objectHasProperty } from '@/lib/helpers/dataHelpers'
import { checkRoadmapPath } from '@/lib/helpers/pathHelpers'
import { roadmapStores } from '@/stores/PostListStore'
import type { ICustomState } from '@/types/organization'
import type { IPost } from '@/types/post'

interface IPropTypes {
  post: IPost
}

const SinglePostStatusChange = observer(({ post }: IPropTypes) => {
  const t = useTranslations('post')
  const router = useRouter()
  const {
    userProfile,
    custom_states,
    organizationCustomization,
    default_module,
    organizationSetting,
  } = useContext(HNContext)
  const [statusValue, setStatusValue] = useState<any>(post?.custom_status)
  const statusModalRef = useRef<IPostStatusChangeModalRef>(null)

  const handleUpdate = (newPost: IPost) => {
    if (newPost.comment_status) {
      EventEmitter.dispatch('ENTITY_UPDATE', {
        entity: ENTITIES.COMMENT,
        actionType: EVENT_ACTION_TYPES.ADD,
        data: {
          id: newPost.comment_status.id,
          data: newPost.comment_status,
        },
      })
    }
    if (checkRoadmapPath(router) || default_module === 'roadmap') {
      roadmapStores[post.status.value]?.deleteSinglePost(post.slug, post)
      roadmapStores[newPost.status.value]?.appendPost({
        ...post,
        ...newPost,
      })
    }
  }

  const handleStatusSelect = (_key: string, value: ISelectOption) => {
    if (value?.length && !value[0]) return null
    const status = custom_states?.find((s: ICustomState) => s.slug === value[0])
    if (!status) return null
    setStatusValue(status)
    return statusModalRef.current?.toggle()
  }

  const renderStatus = () => {
    if (post.can_change_status) {
      if (organizationCustomization?.enable_custom_status)
        return (
          <AdminPostStatusFilter
            filters={{ state: post.custom_status.value }}
            multiple={false}
            onChange={handleStatusSelect}
            behaviour='select'
            placeholder={t('fields.status.placeholder')}
          />
        )
      return (
        <AdminPostParentStatusFilter
          filters={{ status: post.custom_status.value }}
          multiple={false}
          placeholder={t('fields.status.placeholder')}
          onChange={handleStatusSelect}
        />
      )
    }

    if (
      (userProfile?.is_csm_of_organization ||
        organizationSetting?.show_custom_states) &&
      objectHasProperty(post, 'custom_status')
    )
      return <Status status={post.custom_status} />
    return <Status status={post.status} />
  }

  return (
    <div
      className='grid grid-cols-5 items-center gap-4 px-4'
      data-testid='single_post_status_change'
    >
      <Typography.Text className='col-span-2 font-medium'>
        {t('labels.status')}
      </Typography.Text>
      <span className='col-span-3'>{renderStatus()}</span>
      <PostStatusChangeModal
        post={post}
        ref={statusModalRef}
        toStatus={statusValue}
        onUpdate={handleUpdate}
      />
    </div>
  )
})

export default SinglePostStatusChange
